import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../composants/Button/Button';
import TableauDeBord from '../../../../composants/TableauDeBord/TableauDeBord';
import { formatDate } from '../../../../utils/entityUtils';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete.svg';

// ── Barre de progression profil ───────────────────────────────────────────────

export function ProfilProgressBar({ value }: { value: number }) {
  const color = value === 100 ? '#22c55e' : value >= 50 ? '#eab308' : '#ef4444';
  return (
    <div className="profil-progress-wrapper">
      <div className="profil-progress-bar">
        <div
          className="profil-progress-fill"
          style={{ width: `${value}%`, backgroundColor: color }}
        />
      </div>
      <span className="profil-progress-label">{value}%</span>
    </div>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface EntrepriseInfo {
  id: number;
  nom: string;
  siret: string;
  representant?: string;
  email?: string;
  telephone?: string;
  progression: number;
}

interface GroupementMember {
  id: number;
  entreprise: EntrepriseInfo;
  entite_profil?: {
    id: number;
    nom: string;
    siret: string;
    progression: number;
  } | null;
  accepted_at: string | null;
  status: 'pending' | 'accepted' | 'refused';
}

interface SectionCandidatProps {
  entite: EntrepriseInfo;
  groupementMembers: GroupementMember[];
  estMandataire: boolean;
  isSoumise: boolean;
  onRetirer: (entrepriseId: number) => void;
  onInviter: () => void;
  onViewMemberProfile?: (entiteId: number) => void;
}

// ── Section "Candidat" ────────────────────────────────────────────────────────

const SectionCandidat: React.FC<SectionCandidatProps> = ({
  entite,
  groupementMembers,
  estMandataire,
  isSoumise,
  onRetirer,
  onInviter,
  onViewMemberProfile,
}) => {
  const navigate = useNavigate();

  return (
    <>
      <div className="section-header">Candidat</div>
      <div className="section-content">
        {/* Bloc mandataire — disposition horizontale */}
        <div className="mandataire-card">
          <div className="mandataire-info-row">
            <span className="mandataire-name">{entite.nom}</span>
            {entite.siret && <span className="mandataire-field">Siret: {entite.siret}</span>}
            {entite.representant && <span className="mandataire-field">Représentant: {entite.representant}</span>}
            {entite.email && <span className="mandataire-field">Mail: {entite.email}</span>}
            {entite.telephone && <span className="mandataire-field">Tel: {entite.telephone}</span>}
            <ProfilProgressBar value={entite.progression} />
          </div>
          <div className='mandataire-profil-button'>
            <Button
              type="button"
              variant="secondary"
              value="Voir mon profil"
              size="small"
              onClick={() => navigate('/profil')}
            />
          </div>
        </div>


        {/* Table cotraitants */}
        {groupementMembers.length > 0 && (
          <TableauDeBord
            data={groupementMembers.map((member) => ({
              id: member.id,
              entreprise_id: member.entreprise.id,
              nom: member.entreprise.nom,
              siret: member.entreprise.siret,
              representant: member.entreprise.representant,
              email: member.entreprise.email,
              telephone: member.entreprise.telephone,
              progression: member.entreprise.progression,
              status: member.status,
              accepted_at: member.accepted_at
            }))}
            columns={[
              { field: 'nom', header: 'Nom de l\'entreprise' },
              { field: 'siret', header: 'Siret' },
              { field: 'representant', header: 'Représentant' },
              { field: 'email', header: 'Mail' },
              { field: 'telephone', header: 'Tél' },
              {
                field: 'accepted_at',
                header: 'Accepté le',
                renderCell: (value: string | null | Date) => value ? formatDate(value) : '-'
              },
              {
                field: 'progression',
                header: 'Complétion du profil',
                renderCell: (value: number) => <ProfilProgressBar value={value} />
              },
              {
                field: 'profile',
                header: 'Voir le profil complet',
                renderCell: (_value: any, item: any) => (
                  <Button
                    type="button"
                    variant="secondary"
                    value="Voir le profil"
                    size="small"
                    className='view-profile-button'
                    onClick={() => item.entreprise_id && onViewMemberProfile && onViewMemberProfile(item.entreprise_id)}
                    disabled={!item.entreprise_id}
                  />
                )
              },
              ...(estMandataire ? [{

                field: 'actions',
                header: 'Actions',
                renderCell: (_value: any, item: any) => (
                  <div className="member-actions">

                    {item.status === 'accepted' && (

                      <Button
                        type="button"
                        variant="secondary"
                        value=""
                        size="small"
                        className='delete-member-button'
                        icon={<DeleteIcon />}
                        onClick={() => onRetirer(item.id)}
                      />
                    )}

                  </div>
                )
              }] : [])
            ]}
            selectedItems={[]}
            onSelectionChange={() => { }}
            isSelectable={false}
            showActionsColumn={false}
            showPagination={false}
          />
        )}

        {/* Bouton ajouter membre */}
        {
          estMandataire && !isSoumise && (
            <div className="groupement-footer">
              <Button
                type="button"
                variant="primary"
                value="Ajouter un membre au groupement"
                size="small"
                onClick={onInviter}
              />
            </div>
          )
        }
      </div>
    </>
  );
};

export default SectionCandidat;
